<template>
    <b-modal :visible="value" @change="onVisibleChange" :title="title" size="lg" hide-footer>
        <p class="text-muted">
            Generates a statement for this account covering the period below, then renders, archives
            and delivers it through the account's configured channels.
        </p>

        <ARStatementPeriodFields :period-start.sync="periodStart" :period-end.sync="periodEnd"
            :due-by-date.sync="dueByDate" />

        <div class="text-right mt-3">
            <b-button variant="secondary" class="mr-2" :disabled="saving" @click="close">Cancel</b-button>
            <b-button variant="primary" :disabled="saving || !hasValidPeriod" @click="generate">
                <b-spinner v-if="saving" small class="mr-1" />
                Generate Statement
            </b-button>
        </div>
    </b-modal>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import axios from "axios";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import {
    arDefaultDueByDate,
    arDefaultPeriodEnd,
    arDefaultPeriodStart,
    arIsValidStatementPeriod,
} from "@/models/AR";
import ARStatementPeriodFields from "@/components/Patient/ARStatementPeriodFields.vue";

/**
 * On-demand generation of a single account's statement. The period contract is shared with the
 * monthly batch through ARStatementPeriodFields so the two cannot drift on defaults or on what
 * counts as a valid range.
 */
@Component({
    name: "ARGenerateStatementModal",
    components: {
        ARStatementPeriodFields,
    },
})
export default class ARGenerateStatementModal extends Vue {
    @Prop({ type: Boolean, default: false }) value!: boolean;
    @Prop({ default: null }) accountId!: number | null;
    @Prop({ default: "" }) accountNumber!: string;

    private saving = false;
    private periodStart: string = arDefaultPeriodStart();
    private periodEnd: string = arDefaultPeriodEnd();
    private dueByDate: string = arDefaultDueByDate();

    get title(): string {
        return this.accountNumber ? `Generate a Statement for ${this.accountNumber}` : "Generate a Statement";
    }

    get hasValidPeriod(): boolean {
        return arIsValidStatementPeriod(this.periodStart, this.periodEnd);
    }

    // Reopening returns to the default period rather than whatever the last attempt left behind,
    // so a period edited for one account is never silently reused for the next.
    @Watch("value")
    onShow(visible: boolean): void {
        if (!visible) return;
        this.periodStart = arDefaultPeriodStart();
        this.periodEnd = arDefaultPeriodEnd();
        this.dueByDate = arDefaultDueByDate();
    }

    async generate(): Promise<void> {
        if (!this.accountId || !this.hasValidPeriod) return;
        try {
            this.saving = true;
            await axios.post(`/AR/accounts/${this.accountId}/statements`, {
                periodStart: this.periodStart,
                periodEnd: this.periodEnd,
                dueByDate: this.dueByDate,
            });
            this.$notification(NotificationOptions.success("Statement generated."));
            this.$emit("saved");
            this.close();
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.saving = false;
        }
    }

    onVisibleChange(visible: boolean): void {
        this.$emit("input", visible);
    }

    close(): void {
        this.$emit("input", false);
    }
}
</script>

<style scoped></style>
